import type React from 'react';
import { createContext, useContext, useReducer } from 'react';

interface WizardState {
  currentStep: number;
  totalSteps: number;
  isLastStep: boolean;
  data: Record<string, any>;
  isCompleted: boolean;
}

interface WizardAction {
  type: 'SET_STEP' | 'COMPLETE_WIZARD';
  payload?: any;
}

interface WizardContextType extends WizardState {
  goToStep: (step: number) => void;
  completeWizard: () => void;
  canGoNext: boolean;
  canGoPrev: boolean;
}

const WizardContext = createContext<WizardContextType | undefined>(undefined);

const wizardReducer = (state: WizardState, action: WizardAction): WizardState => {
  switch (action.type) {
    case 'SET_STEP':
      return {
        ...state,
        currentStep: Math.max(0, Math.min(action.payload, state.totalSteps - 1)),
      };
    case 'COMPLETE_WIZARD':
      return {
        ...state,
        isCompleted: true,
      };
    default:
      return state;
  }
};

interface WizardProviderProps {
  children: React.ReactNode;
  totalSteps: number;
  initialData?: Record<string, any>;
}

export const WizardProvider: React.FC<WizardProviderProps> = ({ children, totalSteps, initialData = {} }) => {
  const [state, dispatch] = useReducer(wizardReducer, {
    currentStep: 0,
    totalSteps,
    data: initialData,
    isCompleted: false,
    isLastStep: false,
  });

  const goToStep = (step: number) => {
    const validStep = Math.max(0, Math.min(step, state.totalSteps - 1));
    dispatch({ type: 'SET_STEP', payload: validStep });
  };

  const completeWizard = () => dispatch({ type: 'COMPLETE_WIZARD' });

  const canGoNext = state.currentStep < state.totalSteps - 1;
  const canGoPrev = state.currentStep > 0;

  const value: WizardContextType = {
    ...state,
    goToStep,
    completeWizard,
    canGoNext,
    canGoPrev,
    isLastStep: state.currentStep === state.totalSteps - 1,
  };

  return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
};

export const useWizard = (): WizardContextType => {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error('useWizard must be used within a WizardProvider');
  }
  return context;
};
